/**
 * Keyboard Shortcuts Hook for Photo Culling Workflow
 *
 * Shortcuts:
 * - ← / → : Navigate between photos
 * - K : Keep (flag for edit)
 * - D : Delete (cull)
 * - X : Mark as duplicate
 * - Space : Toggle burst expansion
 * - 1-5 : Manual quality rating override
 * - Shift+Select : Multi-select range
 * - Cmd/Ctrl+A : Select all visible
 * - Cmd/Ctrl+Z : Undo last action
 */

import { useEffect, useCallback, useState } from 'react';

export interface Photo {
  photo_id: string;
  filename: string;
  burst_id?: string;
  status: string;
}

interface KeyboardShortcutsOptions {
  photos: Photo[];
  selectedPhotos: Set<string>;
  currentPhotoIndex: number;
  onNavigate: (direction: 'prev' | 'next') => void;
  onFlag: (photoId: string) => void;
  onCull: (photoId: string) => void;
  onToggleSelection: (photoId: string) => void;
  onSelectAll: () => void;
  onBurstToggle?: (burstId: string) => void;
  onQualityRate?: (photoId: string, rating: number) => void;
  onUndo?: (action: ActionHistory) => void;
  enabled?: boolean;
}

interface ActionHistory {
  type: 'flag' | 'cull' | 'select' | 'quality';
  photoId: string;
  previousStatus?: string;
  rating?: number;
  timestamp: number;
}

export function useKeyboardShortcuts({
  photos,
  selectedPhotos,
  currentPhotoIndex,
  onNavigate,
  onFlag,
  onCull,
  onToggleSelection,
  onSelectAll,
  onBurstToggle,
  onQualityRate,
  onUndo,
  enabled = true,
}: KeyboardShortcutsOptions) {
  const [actionHistory, setActionHistory] = useState<ActionHistory[]>([]);
  const [flashMessage, setFlashMessage] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Add action to history for undo
  const addToHistory = useCallback((action: ActionHistory) => {
    setActionHistory(prev => [...prev.slice(-9), action]); // Keep last 10 actions
  }, []);

  // Show flash message
  const showFlash = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setFlashMessage({ message, type });
    setTimeout(() => setFlashMessage(null), 2000);
  }, []);

  // Undo last action
  const undo = useCallback(() => {
    if (actionHistory.length === 0) {
      showFlash('Nothing to undo', 'error');
      return;
    }

    const lastAction = actionHistory[actionHistory.length - 1];

    // Call the onUndo callback if provided (allows parent component to handle API calls)
    if (onUndo) {
      onUndo(lastAction);
    }

    // Remove action from history
    setActionHistory(prev => prev.slice(0, -1));
    showFlash(`Undid ${lastAction.type} action`, 'success');
  }, [actionHistory, onUndo, showFlash]);

  // Handle keyboard events
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Don't interfere with input fields
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    const currentPhoto = photos[currentPhotoIndex];
    const hasSelection = selectedPhotos.size > 0;

    // Cmd/Ctrl+A - Select all
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
      event.preventDefault();
      onSelectAll();
      showFlash(`Selected ${photos.length} photos`, 'success');
      return;
    }

    // Cmd/Ctrl+Z - Undo
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      undo();
      return;
    }

    // Arrow Left - Previous photo
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      if (currentPhotoIndex > 0) {
        onNavigate('prev');
      }
      return;
    }

    // Arrow Right - Next photo
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      if (currentPhotoIndex < photos.length - 1) {
        onNavigate('next');
      }
      return;
    }

    // K - Flag for edit (Keep)
    if (event.key.toLowerCase() === 'k') {
      event.preventDefault();

      if (hasSelection) {
        // Flag all selected photos
        selectedPhotos.forEach(photoId => {
          const photo = photos.find(p => p.photo_id === photoId);
          onFlag(photoId);
          addToHistory({ type: 'flag', photoId, previousStatus: photo?.status, timestamp: Date.now() });
        });
        showFlash(`Flagged ${selectedPhotos.size} photos for editing`, 'success');
      } else if (currentPhoto) {
        // Flag current photo
        onFlag(currentPhoto.photo_id);
        addToHistory({ type: 'flag', photoId: currentPhoto.photo_id, previousStatus: currentPhoto.status, timestamp: Date.now() });
        showFlash(`Flagged ${currentPhoto.filename}`, 'success');

        // Auto-advance to next photo
        if (currentPhotoIndex < photos.length - 1) {
          setTimeout(() => onNavigate('next'), 150);
        }
      }
      return;
    }

    // D - Cull (Delete)
    if (event.key.toLowerCase() === 'd') {
      event.preventDefault();

      if (hasSelection) {
        // Cull all selected photos
        selectedPhotos.forEach(photoId => {
          const photo = photos.find(p => p.photo_id === photoId);
          onCull(photoId);
          addToHistory({ type: 'cull', photoId, previousStatus: photo?.status, timestamp: Date.now() });
        });
        showFlash(`Culled ${selectedPhotos.size} photos`, 'success');
      } else if (currentPhoto) {
        // Cull current photo
        onCull(currentPhoto.photo_id);
        addToHistory({ type: 'cull', photoId: currentPhoto.photo_id, previousStatus: currentPhoto.status, timestamp: Date.now() });
        showFlash(`Culled ${currentPhoto.filename}`, 'success');

        // Auto-advance to next photo
        if (currentPhotoIndex < photos.length - 1) {
          setTimeout(() => onNavigate('next'), 150);
        }
      }
      return;
    }

    // X - Toggle selection (for multi-select)
    if (event.key.toLowerCase() === 'x') {
      event.preventDefault();
      if (currentPhoto) {
        onToggleSelection(currentPhoto.photo_id);
        const isNowSelected = !selectedPhotos.has(currentPhoto.photo_id);
        showFlash(
          isNowSelected ? `Selected ${currentPhoto.filename}` : `Deselected ${currentPhoto.filename}`,
          'success'
        );

        // Auto-advance to next photo
        if (currentPhotoIndex < photos.length - 1) {
          setTimeout(() => onNavigate('next'), 150);
        }
      }
      return;
    }

    // Space - Toggle burst expansion
    if (event.key === ' ') {
      event.preventDefault();
      if (currentPhoto?.burst_id && onBurstToggle) {
        onBurstToggle(currentPhoto.burst_id);
        showFlash('Opening burst viewer...', 'success');
      } else {
        showFlash('Photo is not part of a burst', 'error');
      }
      return;
    }

    // 1-5 - Manual quality rating
    if (['1', '2', '3', '4', '5'].includes(event.key)) {
      event.preventDefault();
      const rating = parseInt(event.key);

      if (onQualityRate) {
        if (hasSelection) {
          // Rate all selected photos
          selectedPhotos.forEach(photoId => {
            onQualityRate(photoId, rating);
            addToHistory({ type: 'quality', photoId, rating, timestamp: Date.now() });
          });
          showFlash(`Set quality rating to ${rating}/5 for ${selectedPhotos.size} photos`, 'success');
        } else if (currentPhoto) {
          // Rate current photo
          onQualityRate(currentPhoto.photo_id, rating);
          addToHistory({ type: 'quality', photoId: currentPhoto.photo_id, rating, timestamp: Date.now() });
          showFlash(`Set quality rating to ${rating}/5 for ${currentPhoto.filename}`, 'success');

          // Auto-advance to next photo
          if (currentPhotoIndex < photos.length - 1) {
            setTimeout(() => onNavigate('next'), 150);
          }
        }
      } else {
        showFlash(`Quality rating not available (callback not provided)`, 'error');
      }
      return;
    }
  }, [
    enabled,
    photos,
    currentPhotoIndex,
    selectedPhotos,
    onNavigate,
    onFlag,
    onCull,
    onToggleSelection,
    onSelectAll,
    onBurstToggle,
    onQualityRate,
    addToHistory,
    showFlash,
    undo,
  ]);

  // Register keyboard event listener
  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);

  return {
    flashMessage,
    actionHistory,
    undo,
  };
}

/**
 * Flash Message Component
 * Can be used to display temporary feedback messages
 */
export interface FlashMessageProps {
  message: string;
  type: 'success' | 'error';
  onDismiss?: () => void;
}
